import React, { useEffect, useState } from 'react';
import './RoletaDescontos.css';

type Sector = { valor: string; cor: string };

const sectoresInit: Sector[] = [
  { valor: '10%', cor: '#ffe8d6' }, // tom bem claro (10% = mais claro)
  { valor: '20%', cor: '#ffb380' }, // tom claro
  { valor: '30%', cor: '#ffe8d6' }, // tom médio claro
  { valor: '40%', cor: '#ffb380' }, // tom médio
  { valor: '50%', cor: '#ffe8d6' }, // tom próximo ao principal
  { valor: '75%', cor: '#ffb380' }, // COR PRINCIPAL (75% = destaque máximo)
];

function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number) {
  const angleRad = (angleDeg - 90) * Math.PI / 180.0;
  return {
    x: cx + (radius * Math.cos(angleRad)),
    y: cy + (radius * Math.sin(angleRad))
  };
}

function describeArc(cx: number, cy: number, radius: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
}

function hexToLuminance(hex: string) {
  // remove #
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  // sRGB companding
  const srgb = [r, g, b].map(v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  // relative luminance
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

type Props = {
  onResult?: (resultado: string) => void;
  onContinue: (resultado: string) => void;
};

const RoletaDescontos: React.FC<Props> = ({ onResult, onContinue }) => {
  const [girando, setGirando] = useState(false);
  const [angle, setAngle] = useState(0); // valor absoluto (acumulado)
  const [resultado, setResultado] = useState<string | null>(null);
  const [jaGirou, setJaGirou] = useState(false); // controle para girar apenas uma vez
  const sectors = sectoresInit;
  const n = sectors.length;
  const sliceAngle = 360 / n;
  const cx = 150, cy = 150, radius = 130; // svg coordinates
  const innerCircleR = 50;

  // Verifica se já girou a roleta ao carregar o componente
  useEffect(() => {
    const roletaJaUtilizada = localStorage.getItem('xeguei_roleta_utilizada');
    const resultadoSalvo = localStorage.getItem('xeguei_roleta_resultado');

    if (roletaJaUtilizada === 'true') {
      setJaGirou(true);
      if (resultadoSalvo) {
        setResultado(resultadoSalvo);
      }
    }
  }, []);

  const spin = () => {
    if (girando || jaGirou) return;
    setGirando(true);
    setJaGirou(true);
    setResultado(null);

    // Salva no localStorage que a roleta foi utilizada
    localStorage.setItem('xeguei_roleta_utilizada', 'true');

    // SEMPRE para fisicamente no 75% mas em posição aleatória dentro do setor
    const indice75 = sectors.findIndex(s => s.valor === '75%');
    const sectorStart = indice75 * sliceAngle;
    const sectorEnd = sectorStart + sliceAngle;

    // Posição aleatória dentro do setor de 75% (não só no centro)
    const randomPositionInSector = sectorStart + (Math.random() * sliceAngle);
    const targetAngle = randomPositionInSector;

    const extraRotations = (5 + Math.floor(Math.random() * 2)) * 360; // 5..6 voltas
    const final = angle + extraRotations + (360 - targetAngle); // gira para posição aleatória no setor 75%

    // aplica rotação acumulada
    setAngle(final);

    // tempo igual ao CSS transition (6s para mais suavidade)
    window.setTimeout(() => {
      setGirando(false);

      // A seta está no topo (0°), então precisamos calcular qual setor está nessa posição
      // Como a roleta gira, mas a seta fica fixa, invertemos o cálculo
      const finalAngleNormalized = (360 - (final % 360)) % 360;
      const sectorIndex = Math.floor(finalAngleNormalized / sliceAngle) % n;
      const resultadoReal = sectors[sectorIndex].valor;

      setResultado(resultadoReal); // mostra o valor do setor onde realmente parou

      // Salva o resultado no localStorage
      localStorage.setItem('xeguei_roleta_resultado', resultadoReal);

      // Chama o callback se fornecido
      if (onResult) {
        onResult(resultadoReal);
      }
    }, 6000);
  };

  return (
    <div className="modal-image-section">
      <div className="modal-roleta-area">
        <div className="roleta-container">
          {/* <h2 className="roleta-title">Gire e ganhe desconto em todas mensalidades!</h2> */}

          <div className="roleta-wrapper">
            <div className="pointer-top" aria-hidden />
            <svg viewBox="0 0 300 300" className="wheel-svg" role="img" aria-label="Roleta">
              <g
                className="wheel-group"
                style={{
                  transform: `rotate(${angle}deg)`,
                  transformOrigin: `${cx}px ${cy}px`,
                  transition: girando ? 'transform 6s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none'
                }}
              >
                {sectors.map((s, i) => {
                  const start = i * sliceAngle;
                  const end = start + sliceAngle;
                  const pathD = describeArc(cx, cy, radius, start, end);

                  // posição do texto (centro da fatia)
                  const mid = start + sliceAngle / 2;
                  const textPos = polarToCartesian(cx, cy, radius * 0.62, mid);

                  // escolher cor do texto para contraste
                  const lum = hexToLuminance(s.cor);
                  const textColor = lum < 0.45 ? '#ffffff' : '#222222';

                  return (
                    <g key={i}>
                      <path d={pathD} fill={s.cor} stroke="#ffffff" strokeWidth="1" />
                      <text
                        x={textPos.x}
                        y={textPos.y}
                        fill={textColor}
                        fontWeight={700}
                        fontSize={14}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          transform: `rotate(${-angle}deg)`,
                          transformOrigin: `${textPos.x}px ${textPos.y}px`,
                          transition: girando ? 'transform 6s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none'
                        }}
                      >
                        {s.valor}
                      </text>
                    </g>
                  );
                })}

                {/* círculo central (visual) */}
                <circle cx={cx} cy={cy} r={innerCircleR} fill="#fff" stroke="#eee" strokeWidth="4" onClick={spin} />
              </g>

              {/* Botão de texto centralizado - não gira com a roleta */}
              <text
                x={cx}
                y={cy}
                textAnchor="middle"
                dominantBaseline="middle"
                fontWeight={700}
                fontSize={16}
                fill={girando || jaGirou ? "#999" : "#ff0000"}
                style={{
                  cursor: girando || jaGirou ? "not-allowed" : "pointer",
                  userSelect: "none"
                }}
                onClick={spin}
              >
                {girando
                  ? ''
                  : jaGirou
                    ? ''
                    : 'GIRAR'
                }
              </text>
            </svg>
          </div>
          {resultado && (
            <p className="result-text">🎉 Parabéns! {resultado} de desconto!</p>
          )}
        </div>
      </div>
      <div className="modal-roleta-card">
        <h2 className="modal-roleta-title">Oferta Especial!</h2>
        <p className="modal-roleta-description">Gire e ganhe desconto em todas as mensalidades!</p>
        <button
          type="button"
          className="modal-cta-button"
          disabled={girando}
          onClick={() => resultado ? onContinue(resultado) : spin()}
        >
          {girando ? 'Girando…' : resultado ? 'Continuar' : 'Girar'}
        </button>
      </div>
    </div>
  );
};

export default RoletaDescontos;
